import React, { useEffect, useState } from 'react';
import { animate, motion } from 'framer-motion';
import { useDb } from '../../../hooks/useDb';
import { useMotionPreference } from '../../../hooks/useMotionPreference';
import { agendaToday, nextEvent } from '../../../utils/dbSelectors';
import { durationLabel, toMinutes } from '../../../utils/time';
interface AgendaArcProps {
  cx: number;
  cy: number;
  /** Anna's visible radius */
  R: number;
}
const T0 = 7 * 60;
const T1 = 19 * 60;
const A0 = -200 * Math.PI / 180;
const A1 = 20 * Math.PI / 180;
const EASE = [0.23, 1, 0.32, 1] as const;
const angleAt = (min: number) => A0 + (Math.min(T1, Math.max(T0, min)) - T0) / (T1 - T0) * (A1 - A0);

/** Today as an arc around Anna: 07:00 left, 19:00 right, a "now" marker travelling over it. */
export function AgendaArc({
  cx,
  cy,
  R
}: AgendaArcProps) {
  const reduce = useMotionPreference();
  const {
    events,
    now,
    next
  } = useDb(t => ({
    events: agendaToday(t),
    now: toMinutes(t.meta.time),
    next: nextEvent(t)
  }));
  const ra = R * 1.16;
  const nowA = angleAt(now);
  const [a, setA] = useState(nowA);
  useEffect(() => {
    if (reduce) {
      setA(nowA);
      return;
    }
    const c = animate(a, nowA, {
      duration: 0.3,
      ease: EASE,
      onUpdate: setA
    });
    return () => c.stop();
  }, [nowA]); // eslint-disable-line react-hooks/exhaustive-deps

  const pt = (ang: number, r = ra) => ({
    x: cx + r * Math.cos(ang),
    y: cy + r * Math.sin(ang)
  });
  const arc = (a0: number, a1: number, r = ra) => {
    const p0 = pt(a0, r);
    const p1 = pt(a1, r);
    return `M ${p0.x} ${p0.y} A ${r} ${r} 0 ${a1 - a0 > Math.PI ? 1 : 0} 1 ${p1.x} ${p1.y}`;
  };
  const marker = pt(a);
  const ticks = [8, 10, 12, 14, 16, 18].map(hr => pt(angleAt(hr * 60)));
  return <motion.div className="pointer-events-none absolute inset-0 z-[2]" initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0,
    transition: {
      duration: 0.25
    }
  }} transition={{
    duration: 0.2
  }} data-id="element-1450" data-mp="1q816wibpf3f">
      <ol className="sr-only" aria-label="Vandaag" data-id="element-1451" data-mp="22crl0v51cdfr">
        {events.map(e => <li key={e.id} data-id="element-1452" data-mp="1yawijnbdbu0e">
            {e.start} {e.title}
            {e.id === next?.id ? `, over ${durationLabel(toMinutes(e.start) - now)}` : ''}
          </li>)}
      </ol>

      <svg className="absolute inset-0 h-full w-full overflow-visible" aria-hidden data-id="element-1453" data-mp="1thlbpm6k3d8h">
        <motion.path d={arc(A0, A1)} fill="none" stroke="rgba(255,255,255,0.16)" strokeWidth={2} strokeLinecap="round" initial={{
        pathLength: reduce ? 1 : 0
      }} animate={{
        pathLength: 1
      }} transition={{
        duration: 0.8,
        ease: [0.65, 0, 0.35, 1]
      }} data-id="element-1454" data-mp="1oiqqkdl49cl7" />
        {a > A0 + 0.01 && <path d={arc(A0, a)} fill="none" strokeWidth={2.5} strokeLinecap="round" style={{
        stroke: 'rgb(var(--accent) / 0.55)'
      }} data-id="element-1455" data-mp="15nafcg61yre2" />}
        {ticks.map((t, i) => <circle key={i} cx={t.x} cy={t.y} r={2.5} fill="rgba(255,255,255,0.35)" data-id="element-1456" data-mp="2zzgtnnu8toal" data-mpi={i} />)}
        {events.map((e, i) => {
        const isNext = e.id === next?.id;
        const stroke = e.kind === 'meeting' ? 'rgb(var(--accent))' : e.kind === 'focus' ? 'rgba(255,255,255,0.22)' : 'rgba(238,242,246,0.7)';
        return <motion.path key={e.id} d={arc(angleAt(toMinutes(e.start)), Math.max(angleAt(toMinutes(e.start)) + 0.02, angleAt(toMinutes(e.end))))} fill="none" strokeWidth={isNext ? 11 : 8} strokeLinecap="round" style={{
          stroke
        }} initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} transition={{
          duration: 0.2,
          delay: reduce ? 0 : 0.3 + i * 0.05
        }} data-id="element-1457" data-mp="3ha443tacmuuw" data-mpi={i} />;
      })}
        <circle cx={marker.x} cy={marker.y} r={16} fill="none" strokeWidth={2} style={{
        stroke: 'rgb(var(--accent) / 0.5)'
      }} data-id="element-1458" data-mp="32ibrvq5upwxe" />
        <circle cx={marker.x} cy={marker.y} r={8} fill="#FFFFFF" data-id="element-1459" data-mp="htxhzgabxoxe" />
      </svg>

      {events.filter(e => e.kind !== 'focus').map((e, i) => {
      const mid = angleAt((toMinutes(e.start) + toMinutes(e.end)) / 2);
      const p = pt(mid, ra + 30);
      const c = Math.cos(mid);
      const s = Math.sin(mid);
      const tx = c < -0.3 ? '-100%' : c > 0.3 ? '0%' : '-50%';
      const ty = s < -0.3 ? '-100%' : s > 0.3 ? '0%' : '-50%';
      const isNext = e.id === next?.id;
      return <motion.div key={e.id} className={`absolute whitespace-nowrap ${c < -0.3 ? 'text-right' : ''}`} style={{
        left: p.x,
        top: p.y,
        transform: `translate(${tx}, ${ty})`
      }} initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : 0.45 + i * 0.06
      }} data-id="element-1460" data-mp="zabdxaglfl3s" data-mpi={i}>
              <p className={`text-[22px] ${isNext ? 'text-ink' : 'text-ink-soft'}`} data-id="element-1461" data-mp="1i36vh65iiwo9">
                <span className="font-semibold tabular-nums" data-id="element-1462" data-mp="1e1tqeya8uxie">{e.start}</span> {e.title.split(',')[0]}
              </p>
              {isNext && <p className="text-[22px] text-accent" data-id="element-1463" data-mp="1wpvtf3k491sj">over {durationLabel(toMinutes(e.start) - now)}</p>}
            </motion.div>;
    })}
    </motion.div>;
}